import { useRowSelect } from '@table-library/react-table-library/select';
import { Action, State } from '@table-library/react-table-library/types/common';
import { Button, TableItem } from 'components/atoms';
import { FormInput } from 'components/molecules';
import { QueryFilter, Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useComponentVisible } from 'hooks';
import { useTranslation } from 'react-i18next';
import { PiUserPlus } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { useGetClientAccounts } from 'services/client-account';
import {
  IClientAccountInfiniteResponse,
  IGetClientAccountsParams,
} from 'services/client-account/types';
import { useSearchById, useSetSearchById } from 'store/state';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { getNodes } from 'utils/table/nodes.utils';

const TABLE_ID = 'ClientAccounts';

const initialValues: IGetClientAccountsParams = {
  labelName: '',
};

const ClientAccounts = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const i18nextPrefix = 'client-account';

  const { ref, isComponentVisible, handleComponentVisible } =
    useComponentVisible<HTMLDivElement>(false, true, TABLE_ID);

  const searchById = useSearchById();
  const setSearchById = useSetSearchById();
  const filters = searchById[TABLE_ID] || initialValues;

  const accounts = useGetClientAccounts(filters);

  const tableData = getNodes<IClientAccountInfiniteResponse>(
    accounts.data?.pages.flat()
  );

  const onSelectChange = (action: Action, state: State) => {
    navigate(`${state.id ?? state.ids[state.ids.length - 1]}/users`);
  };

  const select = useRowSelect(tableData, {
    onChange: onSelectChange,
  });

  return (
    <TopbarPage
      topbar={
        <TopBar
          title={t(`client-account.title.list`)}
          hideBackButton
          topbarContent={
            <>
              <QueryFilter.ButtonOpener
                i18nextPrefix={i18nextPrefix}
                onClick={handleComponentVisible}
                variant={BUTTON_VARIANT.default}
                isClear
              />
              <Button
                variant={BUTTON_VARIANT.submit}
                onClick={() => navigate('create/users', { replace: false })}>
                <Button.Text text={t('button.add')} />
                <Button.Icon icon={<PiUserPlus />} />
              </Button>
            </>
          }>
          <QueryFilter<IGetClientAccountsParams>
            i18nextPrefix={i18nextPrefix}
            initialValues={filters}>
            <>
              <QueryFilter.Form<IGetClientAccountsParams>
                ref={ref}
                isOpen={isComponentVisible}
                onDebouncedFormChange={values =>
                  setSearchById(TABLE_ID, values)
                }>
                <QueryFilter.InputsGrid>
                  <FormInput.Text
                    name='labelName'
                    label={t('input.label.name')}
                  />
                </QueryFilter.InputsGrid>
              </QueryFilter.Form>
            </>
          </QueryFilter>
        </TopBar>
      }>
      <Table
        id={TABLE_ID}
        select={select}
        data={tableData}
        loading={accounts.isLoading || accounts.isFetchingNextPage}
        onFullyScroll={accounts.fetchNextPage}
        displayMode
        columns={[
          {
            label: t('input.label.name'),
            renderCell: ({ labelName }) => (
              <TableItem>
                <TableItem.Text text={labelName} />
              </TableItem>
            ),
            resize: true,
          },
        ]}
      />
    </TopbarPage>
  );
};

export default ClientAccounts;
